// Rewards subview — catalog of redeemable items. Stars earned from
// both chore charts feed a lifetime balance that's spent here.
//
// Read paths:
//   - rewards.list                — catalog
//   - rewards.balance             — caller's spendable balance
//   - rewards.pendingRedemptions  — household-wide pending queue
//
// Write paths:
//   - rewards.create / update / archive — catalog management
//   - rewards.redeem                    — flips status to pending or
//                                          auto-approved based on the
//                                          reward's flag
//   - rewards.approve / deny            — parent acts on pending
//
// Same surface for adults + kids (this component is reused by
// KidApp via a thin wrapper). Edit/admin affordances hide for
// kid scope.

import { useEffect, useState } from "preact/hooks";
import { Icon } from "../../icons";
import {
  api,
  type Reward,
  type PendingRedemption,
  type MyRedemption,
} from "../../lib/rpc";
import { confirm } from "../../components/confirm";

interface RewardsViewProps {
  notify: (msg: string) => void;
  /** When true (kid scope), edit/create/archive UI is hidden;
   *  parents see all of it. The redeem action is available to
   *  both. */
  readOnly?: boolean;
}

const REWARD_ICONS = [
  "🎮", "🍦", "🍕", "🍪", "🍩", "🍫", "🎬", "🎨",
  "📚", "🎁", "💵", "⏱️", "🎢", "🏊", "🚴", "🎯",
  "🎵", "🎲", "🎟️", "🦄", "🌟", "⭐", "💎", "🏆",
];

export function RewardsView({ notify, readOnly = false }: RewardsViewProps) {
  const [rewards, setRewards] = useState<Reward[] | null>(null);
  const [balance, setBalance] = useState<{ earned: number; spent: number; balance: number }>({ earned: 0, spent: 0, balance: 0 });
  const [pending, setPending] = useState<PendingRedemption[]>([]);
  const [recent, setRecent] = useState<MyRedemption[]>([]);
  const [newOpen, setNewOpen] = useState(false);
  const [editing, setEditing] = useState<Reward | null>(null);

  const refresh = async () => {
    try {
      const [list, bal, pend, mine] = await Promise.all([
        api.rewardsList(),
        api.rewardsBalance(),
        api.rewardsPendingRedemptions().catch(() => [] as PendingRedemption[]),
        api.rewardsMyRedemptions(10).catch(() => [] as MyRedemption[]),
      ]);
      setRewards(list);
      setBalance(bal);
      setPending(pend);
      setRecent(mine);
    } catch (e: any) {
      notify(e?.message || "couldn't load rewards");
      setRewards([]);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const onRedeem = async (r: Reward) => {
    if (balance.balance < r.costStars) {
      notify(`Need ${r.costStars - balance.balance} more stars`);
      return;
    }
    try {
      const res = await api.rewardsRedeem(r.id);
      notify(res.pendingApproval ? "Sent to parent" : `Redeemed — enjoy ${r.name}!`);
      await refresh();
    } catch (e: any) {
      const msg = e?.message || "couldn't redeem";
      if (msg === "weekly_cap_reached") notify("You've hit the weekly limit on this one");
      else if (msg === "insufficient_stars") notify("Not enough stars yet");
      else notify(msg);
    }
  };

  const onApprove = async (rr: PendingRedemption) => {
    try {
      await api.rewardsApproveRedemption(rr.id);
      notify(`Approved · ⭐ ${rr.costStars} to ${rr.redeemedBy}`);
      await refresh();
    } catch (e: any) { notify(e?.message || "couldn't approve"); }
  };

  const onDeny = async (rr: PendingRedemption) => {
    const ok = await confirm({
      title: `Deny ${rr.redeemedBy}'s request for ${rr.rewardName}?`,
      message: "Refunds the stars. No penalty.",
      confirmLabel: "Deny",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.rewardsDenyRedemption(rr.id);
      notify("Refunded");
      await refresh();
    } catch (e: any) { notify(e?.message || "couldn't deny"); }
  };

  const onArchive = async (r: Reward) => {
    const ok = await confirm({
      title: `Archive "${r.name}"?`,
      message: "Stops being redeemable. Past redemptions stay.",
      confirmLabel: "Archive",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.rewardsArchive(r.id);
      notify("Archived");
      await refresh();
    } catch (e: any) { notify(e?.message || "couldn't archive"); }
  };

  return (
    <div className="rewards">
      {/* Balance hero */}
      <div className="rewards-hero">
        <div className="rewards-balance">⭐ {balance.balance}</div>
        <div className="rewards-balance-sub">
          {balance.earned} earned · {balance.spent} spent
        </div>
        {!readOnly && (
          <button
            className="btn rewards-new-btn"
            type="button"
            onClick={() => setNewOpen(true)}
          >
            <Icon name="plus" size={13} /> New reward
          </button>
        )}
      </div>

      {/* Parent pending-redemption queue */}
      {!readOnly && pending.length > 0 && (
        <section className="rewards-pending">
          <header className="rewards-section-head">
            <div className="eyebrow rewards-amber">{pending.length} waiting for approval</div>
          </header>
          {pending.map((rr) => (
            <div key={rr.id} className="rewards-pending-row">
              <span className="rewards-pending-icon">{rr.rewardIcon || "🎁"}</span>
              <div className="rewards-pending-body">
                <div className="rewards-pending-name">
                  <strong>{rr.redeemedBy}</strong> wants {rr.rewardName}
                </div>
                {rr.note && <div className="rewards-pending-note">"{rr.note}"</div>}
              </div>
              <span className="rewards-pending-cost">⭐ {rr.costStars}</span>
              <button className="rewards-approve" type="button" onClick={() => onApprove(rr)}>Approve</button>
              <button className="rewards-deny" type="button" onClick={() => onDeny(rr)}>Deny</button>
            </div>
          ))}
        </section>
      )}

      {/* Catalog */}
      {rewards === null ? (
        <div className="rewards-loading">loading…</div>
      ) : rewards.length === 0 ? (
        <div className="rewards-empty">
          <div className="rewards-empty-icon">🎁</div>
          <div className="rewards-empty-title">No rewards yet</div>
          {readOnly
            ? <div className="rewards-empty-sub">Ask a grown-up to add some.</div>
            : <div className="rewards-empty-sub">Tap "New reward" to add the first.</div>}
        </div>
      ) : (
        <div className="rewards-grid">
          {rewards.map((r) => (
            <RewardCard
              key={r.id}
              reward={r}
              affordable={balance.balance >= r.costStars}
              readOnly={readOnly}
              onRedeem={() => onRedeem(r)}
              onEdit={readOnly ? undefined : () => setEditing(r)}
            />
          ))}
        </div>
      )}

      {/* My recent redemptions */}
      {recent.length > 0 && (
        <section className="rewards-recent">
          <header className="rewards-section-head">
            <div className="eyebrow">Your recent</div>
          </header>
          {recent.map((m) => (
            <div key={m.id} className="rewards-recent-row" data-status={m.status}>
              <span className="rewards-recent-icon">{m.rewardIcon || "🎁"}</span>
              <span className="rewards-recent-name">{m.rewardName}</span>
              <span className="rewards-recent-cost">⭐ {m.costStars}</span>
              <span className="rewards-recent-status">{m.status}</span>
            </div>
          ))}
        </section>
      )}

      {newOpen && !readOnly && (
        <RewardForm
          mode="create"
          onCancel={() => setNewOpen(false)}
          onSaved={async () => { setNewOpen(false); await refresh(); notify("Added"); }}
          notify={notify}
        />
      )}
      {editing && !readOnly && (
        <RewardForm
          mode="edit"
          reward={editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await refresh(); notify("Saved"); }}
          onArchive={async () => { await onArchive(editing); setEditing(null); }}
          notify={notify}
        />
      )}
    </div>
  );
}

// ── Single reward card ──────────────────────────────────────────

interface RewardCardProps {
  reward: Reward;
  affordable: boolean;
  readOnly: boolean;
  onRedeem: () => void;
  onEdit?: () => void;
}

function RewardCard({ reward, affordable, readOnly, onRedeem, onEdit }: RewardCardProps) {
  return (
    <div className="rewards-card">
      {!readOnly && onEdit && (
        <button className="rewards-card-edit" type="button" onClick={onEdit} title="Edit">
          <Icon name="edit" size={11} />
        </button>
      )}
      <div className="rewards-card-icon">{reward.icon || "🎁"}</div>
      <div className="rewards-card-name">{reward.name}</div>
      {reward.description && (
        <div className="rewards-card-desc">{reward.description}</div>
      )}
      <div className="rewards-card-cost">⭐ {reward.costStars}</div>
      <button
        type="button"
        className="rewards-card-redeem"
        onClick={onRedeem}
        disabled={!affordable}
      >
        {affordable
          ? (reward.requiresApproval ? "Request" : "Redeem")
          : `Need ${reward.costStars} ⭐`}
      </button>
      <div className="rewards-card-meta">
        {reward.requiresApproval && <span>needs approval</span>}
        {reward.maxPerWeek != null && (
          <>
            {reward.requiresApproval && <span className="rewards-card-dot">·</span>}
            <span>max {reward.maxPerWeek}/wk</span>
          </>
        )}
      </div>
    </div>
  );
}

// ── New/edit reward form ─────────────────────────────────────────

interface RewardFormProps {
  mode: "create" | "edit";
  reward?: Reward;
  onCancel: () => void;
  onSaved: () => void;
  onArchive?: () => void;
  notify: (msg: string) => void;
}

function RewardForm({ mode, reward, onCancel, onSaved, onArchive, notify }: RewardFormProps) {
  const [name, setName] = useState(reward?.name || "");
  const [description, setDescription] = useState(reward?.description || "");
  const [icon, setIcon] = useState(reward?.icon || "🎁");
  const [costStars, setCostStars] = useState(reward?.costStars ?? 5);
  const [requiresApproval, setRequiresApproval] = useState(reward?.requiresApproval ?? true);
  const [maxPerWeek, setMaxPerWeek] = useState<number | null>(reward?.maxPerWeek ?? null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const onSave = async () => {
    const t = name.trim();
    if (!t) { notify("Name required"); return; }
    setSaving(true);
    try {
      if (mode === "create") {
        await api.rewardsCreate({
          name: t,
          description: description.trim() || null,
          icon,
          costStars,
          requiresApproval,
          maxPerWeek: maxPerWeek ?? null,
        });
      } else if (reward) {
        await api.rewardsUpdate(reward.id, {
          name: t,
          description: description.trim() || null,
          icon,
          costStars,
          requiresApproval,
          maxPerWeek: maxPerWeek ?? null,
        });
      }
      onSaved();
    } catch (e: any) {
      notify(e?.message || "save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="confirm-bg" onClick={onCancel}>
      <div className="rewards-form" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Reward">
        <header className="rewards-form-head">
          <h3>{mode === "create" ? "New reward" : "Edit reward"}</h3>
          <button className="icon-btn" type="button" onClick={onCancel}>
            <Icon name="x" size={12} />
          </button>
        </header>

        <label className="rewards-form-field">
          <span className="rewards-form-label">Name</span>
          <input
            className="input"
            value={name}
            onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
            placeholder="e.g. Ice cream, 30 min screen time"
            maxLength={80}
            disabled={saving}
            autoFocus
          />
        </label>

        <label className="rewards-form-field">
          <span className="rewards-form-label">Description (optional)</span>
          <input
            className="input"
            value={description}
            onInput={(e) => setDescription((e.currentTarget as HTMLInputElement).value)}
            placeholder="Any extra context"
            maxLength={240}
            disabled={saving}
          />
        </label>

        <div className="rewards-form-field">
          <span className="rewards-form-label">Icon</span>
          <div className="rewards-form-icons">
            {REWARD_ICONS.map((e) => (
              <button
                key={e}
                type="button"
                className={`rewards-form-icon${icon === e ? " is-selected" : ""}`}
                onClick={() => setIcon(e)}
              >{e}</button>
            ))}
          </div>
        </div>

        <div className="rewards-form-grid">
          <label>
            <span className="rewards-form-label">Cost (stars)</span>
            <input
              className="input"
              type="number"
              min={1}
              max={1000}
              value={costStars}
              onInput={(e) => setCostStars(Math.max(1, Math.min(1000, Number((e.currentTarget as HTMLInputElement).value) || 1)))}
              disabled={saving}
            />
          </label>
          <label>
            <span className="rewards-form-label">Max per week (optional)</span>
            <input
              className="input"
              type="number"
              min={0}
              max={100}
              value={maxPerWeek ?? ""}
              placeholder="no limit"
              onInput={(e) => {
                const v = (e.currentTarget as HTMLInputElement).value;
                setMaxPerWeek(v === "" ? null : Math.max(0, Math.min(100, Number(v) || 0)));
              }}
              disabled={saving}
            />
          </label>
        </div>

        <label className="rewards-form-toggle">
          <input
            type="checkbox"
            checked={requiresApproval}
            onChange={(e) => setRequiresApproval((e.currentTarget as HTMLInputElement).checked)}
            disabled={saving}
          />
          <span>Requires parent approval</span>
        </label>

        <div className="rewards-form-actions">
          {mode === "edit" && onArchive && (
            <button className="rewards-form-archive" type="button" onClick={onArchive} disabled={saving}>
              Archive
            </button>
          )}
          <span className="rewards-form-spacer" />
          <button className="rewards-form-cancel" type="button" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="rewards-form-save" type="button" onClick={onSave} disabled={saving || !name.trim()}>
            {mode === "create" ? "Add reward" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
